import React, { useState, useEffect } from "react";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import MDBox from "components/MDBox";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Spinner from "components/Spinner/Spinner";
import ErrorMessage from "components/ErrorMessage/ErrorMessage";
import { getRorMatchingReport } from "helpers/dashboard";

const METRIC_COLUMNS = [
  {
    key: "total",
    label: "Total",
    pct: false,
    tooltip: "All rows in the table. Every occurrence counts, so the same institution or funder name appearing on many records is counted every time.",
  },
  {
    key: "total_enriched",
    label: "Enriched",
    pct: true,
    tooltip: "Rows that were successfully matched to a ROR ID by the enrichment pipeline. Percentage is relative to Total.",
  },
  {
    key: "total_no_match",
    label: "No match",
    pct: true,
    tooltip: "Rows the enrichment pipeline processed but could not match to any ROR organization. Percentage is relative to Total.",
  },
  {
    key: "total_pending",
    label: "Pending",
    pct: true,
    tooltip: "Rows the enrichment pipeline has not processed yet. They will move to Enriched or No match once the scheduled enrichment picks them up. Percentage is relative to Total.",
  },
  {
    key: "unique_total",
    label: "Unique",
    pct: false,
    tooltip: "Distinct institution/funder names in the table, regardless of enrichment state. A name appearing on 500 rows counts once here.",
  },
  {
    key: "unique_enriched",
    label: "Unique enriched",
    pct: true,
    tooltip: "Distinct names that were matched to a ROR ID on at least one row. Percentage is relative to Unique. A name can also appear under Unique no match or Unique pending if other rows with the same name ended up in those states, so the unique percentages can add up to more than 100%.",
  },
  {
    key: "unique_no_match",
    label: "Unique no match",
    pct: true,
    tooltip: "Distinct names with at least one row that enrichment processed without finding a ROR match. Percentage is relative to Unique. High values here point at names the matcher struggles with.",
  },
  {
    key: "unique_pending",
    label: "Unique pending",
    pct: true,
    tooltip: "Distinct names with at least one row still waiting for enrichment. Percentage is relative to Unique.",
  },
];

function MetricCell({ metrics, column }) {
  const value = metrics?.[column.key];
  if (value === undefined || value === null) {
    return <TableCell align="right">—</TableCell>;
  }

  const pct = column.pct ? metrics?.[`${column.key}_pct`] : null;
  return (
    <TableCell align="right">
      {value.toLocaleString()}
      {pct !== null && pct !== undefined && (
        <Typography variant="caption" color="textSecondary" display="block">
          {pct}%
        </Typography>
      )}
    </TableCell>
  );
}

function RorMatchingReport() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [report, setReport] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(false);
      try {
        const response = await getRorMatchingReport();
        setReport(response.data);
      } catch (e) {
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) return <Spinner />;
  if (error) return <ErrorMessage />;

  const tables = report?.tables ?? {};

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <Card>
          <MDBox p={3}>
            <Typography variant="h5" gutterBottom>
              ROR matching report
            </Typography>
            <Typography variant="caption" color="textSecondary" gutterBottom display="block">
              {report?.snapshot_date
                ? `as of ${new Date(report.snapshot_date).toLocaleDateString()} — refreshed nightly`
                : "no snapshot available yet"}
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead sx={{ display: "table-header-group" }}>
                  <TableRow>
                    <TableCell>Table</TableCell>
                    {METRIC_COLUMNS.map((column) => (
                      <TableCell key={column.key} align="right">
                        <MDBox display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
                          {column.label}
                          <Tooltip title={column.tooltip} arrow placement="top">
                            <Icon fontSize="small" sx={{ color: "text.secondary", cursor: "help" }}>
                              info_outlined
                            </Icon>
                          </Tooltip>
                        </MDBox>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(tables).map(([tableName, metrics]) => (
                    <TableRow key={tableName}>
                      <TableCell component="th" scope="row">
                        {tableName}
                      </TableCell>
                      {METRIC_COLUMNS.map((column) => (
                        <MetricCell key={column.key} metrics={metrics} column={column} />
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <MDBox mt={2}>
              <Typography variant="caption" color="textSecondary">
                Enriched: matched to a ROR ID. No match: enrichment ran but found no ROR ID.
                Pending: enrichment has not run yet. Unique counts are per institution/funder
                name — a name can appear in more than one bucket.
              </Typography>
            </MDBox>
          </MDBox>
        </Card>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default RorMatchingReport;
